// M004-F0745 (docs/design/M004-F0744-F0747-Envelope-Workspace.md section 6, wire in section 9) - the pure model of "Move receipts":
// who may give and take receipts, the optimistic state transitions (both lists, both sidebar counts and totals), the merge of the
// Ledger's answer, the selection arithmetic (range, next focus) and the sentences. No React, no I/O: T-M004-ENVUX-005 proves it in
// envelopeMoveModel.test.js; the browser behaviour is proven in tests/envelopes-move-contract.spec.ts.
import { confirmedAmount, isUnsorted, normaliseEnvelope, receiptVat, rowKey, UNSORTED_LABEL } from './envelopeWorkspaceModel';
import { sortReceipts } from './envelopeModel';

export const MOVE_LIMIT = 50; // Spec EnvelopeMoveRequest: receipt_document_ids maxItems 50
export const UNDO_WINDOW_MS = 6000;
export const LOCKED_HINT = 'Submitted — returned envelopes unlock';
export const OFFLINE_HINT = 'Ledger is not reachable — moves are off until it is back.';
export const LIMIT_TEXT = `You can move up to ${MOVE_LIMIT} receipts at a time. Select fewer and try again.`;

const plural = (count) => `${count} ${count === 1 ? 'receipt' : 'receipts'}`;
export const envelopeLabel = (record) => (isUnsorted(record) ? UNSORTED_LABEL : record?.name || 'envelope');

// Why this envelope cannot take receipts ('' = it can). A returned envelope is unlocked and behaves as open (section 6.5).
export const takeReason = (record, { offline = false } = {}) => {
  if (offline) return OFFLINE_HINT;
  if (!record) return 'No such envelope.';
  if (record.pending) return 'Creating… you can drop receipts here in a moment.';
  if (record.locked) return LOCKED_HINT;
  if (record.status === 'closed') return 'Closed — reopen it to add receipts.';
  if (record.status === 'deleted') return 'Deleted — restore it to add receipts.';
  return '';
};

// Why receipts cannot be taken OUT of this envelope ('' = they can): tiles of a locked envelope cannot be dragged out or edited.
export const giveReason = (record, { offline = false } = {}) => {
  if (offline) return OFFLINE_HINT;
  if (!record) return 'No such envelope.';
  if (record.pending) return 'Creating… you can move receipts in a moment.';
  if (record.locked) return LOCKED_HINT;
  if (record.status === 'closed') return 'Closed — reopen it to move receipts out.';
  if (record.status === 'deleted') return 'Deleted — restore it to move receipts out.';
  return '';
};

// The "Move to…" list: Unsorted first, then the rest in sidebar order; the source and deleted envelopes are not offered; a locked,
// closed or still-being-created envelope is listed disabled with the reason.
export const moveTargets = (records, fromId, { offline = false } = {}) => {
  const rest = (Array.isArray(records) ? records : []).filter((record) => record.envelope_id !== fromId && record.status !== 'deleted');
  const ordered = rest.filter(isUnsorted).concat(rest.filter((record) => !isUnsorted(record)));
  return ordered.map((record) => ({ key: rowKey(record), id: record.envelope_id, label: envelopeLabel(record), reason: takeReason(record, { offline }) }));
};

export const moveSentence = (count, name) => `Moved ${plural(count)} to ${name}.`;
export const alreadyThereSentence = (count, name) => `${count === 1 ? 'That receipt is' : 'Those receipts are'} already in ${name}.`;
export const undoneSentence = (count, name) => `Moved ${plural(count)} back to ${name}.`;

// Remove `ids` from the source list and add them (sorted as the Ledger sorts) to the destination list when it is loaded.
// `known` are receipts the caller already holds (an Undo moves back receipts whose current list may not be loaded).
export const applyMoveToDetails = (details, fromId, toId, ids, known = []) => {
  const wanted = new Set(ids);
  const source = details?.[fromId];
  const found = (source?.receipts || []).filter((item) => wanted.has(item.receipt_document_id));
  const have = new Set(found.map((item) => item.receipt_document_id));
  const moved = found.concat(known.filter((item) => wanted.has(item.receipt_document_id) && !have.has(item.receipt_document_id)));
  const next = { ...details };
  if (source) next[fromId] = { ...source, receipts: source.receipts.filter((item) => !wanted.has(item.receipt_document_id)) };
  const target = details?.[toId];
  if (target && Array.isArray(target.receipts)) {
    const present = new Set(target.receipts.map((item) => item.receipt_document_id));
    const added = moved.filter((item) => !present.has(item.receipt_document_id)).map((item) => ({ ...item, envelope_id: toId }));
    next[toId] = { ...target, receipts: sortReceipts(target.receipts.concat(added)) };
  }
  return { details: next, moved };
};

// Shift a record's server `totals` rows by `sign` x these receipts (confirmed amounts per currency; unconfirmed ones on the first row).
const shiftTotals = (totals, receipts, sign) => {
  if (!Array.isArray(totals)) return totals;
  const rows = totals.map((row) => ({ ...row }));
  let unconfirmed = 0;
  receipts.forEach((item) => {
    const amount = confirmedAmount(item);
    if (!amount) { unconfirmed += 1; return; }
    let row = rows.find((entry) => String(entry.currency).toUpperCase() === amount.currency);
    if (!row) { row = { currency: amount.currency, count: 0, total_minor: 0, vat_minor: 0, unconfirmed: 0 }; rows.push(row); }
    row.count = (Number(row.count) || 0) + sign;
    row.total_minor = (Number(row.total_minor) || 0) + sign * amount.minor;
    row.vat_minor = (Number(row.vat_minor) || 0) + sign * receiptVat(item);
  });
  if (unconfirmed) {
    if (!rows.length) rows.push({ currency: 'GBP', count: 0, total_minor: 0, vat_minor: 0, unconfirmed: 0 });
    rows[0].unconfirmed = Math.max(0, (Number(rows[0].unconfirmed) || 0) + sign * unconfirmed);
  }
  return rows;
};

// Both sidebar rows follow at once: counts and server totals move by exactly the receipts that moved.
export const applyMoveToRecords = (records, fromId, toId, moved) => (records || []).map((record) => {
  if (record.envelope_id !== fromId && record.envelope_id !== toId) return record;
  const sign = record.envelope_id === toId ? 1 : -1;
  const counts = record.counts || { receipts: 0, bytes: 0 };
  const bytes = moved.reduce((sum, item) => sum + (Number(item.size) || 0), 0);
  return {
    ...record,
    counts: { ...counts, receipts: Math.max(0, (Number(counts.receipts) || 0) + sign * moved.length), bytes: Math.max(0, (Number(counts.bytes) || 0) + sign * bytes) },
    totals: shiftTotals(record.totals, moved, sign),
  };
});

// The Ledger's answer is the truth for both sides: `{ envelope_id, counts, totals }` replaces the optimistic numbers.
export const mergeMoveSide = (records, side) => (records || []).map((record) => (
  side?.envelope_id && record.envelope_id === side.envelope_id
    ? normaliseEnvelope({ ...record, counts: side.counts || record.counts, totals: Array.isArray(side.totals) ? side.totals : record.totals })
    : record
));

export const rangeIds = (order, a, b) => {
  const from = order.indexOf(a);
  const to = order.indexOf(b);
  if (from < 0 || to < 0) return to < 0 ? [] : [b];
  return order.slice(Math.min(from, to), Math.max(from, to) + 1);
};

// After `removed` leave the list, focus the tile that took the first removed tile's place; else the one before it; else nothing.
export const nextFocusId = (order, removed) => {
  const gone = new Set(removed);
  const first = order.findIndex((id) => gone.has(id));
  if (first < 0) return null;
  const after = order.slice(first).find((id) => !gone.has(id));
  if (after) return after;
  return [...order.slice(0, first)].reverse().find((id) => !gone.has(id)) || null;
};

// M004-F0794: a move refused 404 ENVELOPE_RECEIPT_NOT_FOUND. `elsewhereName`: the current name of the envelope the receipt is now in
// ('' when that name is not known). `found` false: it is in neither envelope (deleted or claim-locked away).
export const STALE_MOVE_CODE = 'ENVELOPE_RECEIPT_NOT_FOUND'; // HTTP 404, what Core passes through today
export const MOVED_MOVE_CODE = 'ENVELOPE_RECEIPT_MOVED'; // HTTP 409 + {current_envelope_id: string|null}, from Spec 1.7.0
export const staleMoveSentence = ({ found, elsewhereName = '' }) => {
  if (!found) return 'This receipt is no longer available.';
  return elsewhereName ? `This receipt was moved elsewhere (to ${elsewhereName}) — refreshed.` : 'This receipt was moved elsewhere — refreshed.';
};
